import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomBreadcrumb, CustomInput, CustomButton, CustomModal, CustomLoading } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, TableActionMenu, ConfirmationModal, ImportSummaryModal, ImportPreviewModal } from '@/shared/components/molecules';
import { Home, Users, Pencil, Trash2, UserPlus, Plus, GraduationCap, ChevronDown, ChevronRight, CornerDownRight, Download, Upload, Search, Check, X } from 'lucide-react';
import { parentApi } from '../api/parentApi';
import { Parent } from '../types';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';

const initials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length > 1) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

export const ListParents = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [parents, setParents] = useState<Parent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);

    // Search and Sort states
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const [isDownloading, setIsDownloading] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [isImportPreviewOpen, setIsImportPreviewOpen] = useState(false);
    const importFileInputRef = useRef<HTMLInputElement>(null);
    const { showToast } = useToastStore();

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    // Add student modal state
    const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
    const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
    const [selectedParentName, setSelectedParentName] = useState<string>('');
    const [isAddingStudent, setIsAddingStudent] = useState(false);

    // Search and link student states
    const [searchAdmission, setSearchAdmission] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchedStudents, setSearchedStudents] = useState<any[]>([]);
    const [searchedStudent, setSearchedStudent] = useState<any | null>(null);
    const [isStudentSelected, setIsStudentSelected] = useState(false);
    const [searchPerformed, setSearchPerformed] = useState(false);

    // Edit parent modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedParent, setSelectedParent] = useState<Parent | null>(null);
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editSecondaryPhone, setEditSecondaryPhone] = useState('');
    const [editAddress, setEditAddress] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    // Delete parent state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [parentToDelete, setParentToDelete] = useState<Parent | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);



    // Delete student state
    const [isDeleteStudentModalOpen, setIsDeleteStudentModalOpen] = useState(false);
    const [studentToDelete, setStudentToDelete] = useState<any | null>(null);
    const [isDeletingStudent, setIsDeletingStudent] = useState(false);

    // Validation errors state
    const [editErrors, setEditErrors] = useState<Record<string, string>>({});

    // Import status modal state
    const [isImportSummaryOpen, setIsImportSummaryOpen] = useState(false);
    const [importSummaryData, setImportSummaryData] = useState<{
        success: number;
        failed: number;
        errors: string[];
        fileName: string;
    } | null>(null);

    const schoolSlug = user?.school?.slug || '';

    const fetchParents = useCallback(async () => {
        if (!schoolSlug) return;
        setIsLoading(true);
        try {
            const response = await parentApi.getParents(schoolSlug, {
                page,
                page_size: pageSize,
                search: debouncedSearch.trim() || undefined,
                sort_by: sortBy,
                sort_order: sortOrder
            });
            if (response.success && response.data) {
                const data = response.data as any;
                let items: Parent[] = [];
                if (Array.isArray(data)) {
                    items = data;
                    setTotalCount(data.length);
                    setTotalPages(1);
                    setHasNext(false);
                    setHasPrevious(false);
                } else {
                    items = data.items || [];
                    setTotalCount(data.total_count || 0);
                    setTotalPages(data.total_pages || 1);
                    setHasNext(data.has_next || false);
                    setHasPrevious(data.has_previous || false);
                }
                const parentsWithId = items.map(p => ({ ...p, id: p.user_id }));
                setParents(parentsWithId);
            }
        } catch (error) {
            showToast('Failed to load parents', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [schoolSlug, page, pageSize, debouncedSearch, sortBy, sortOrder, showToast]);

    useEffect(() => {
        fetchParents();
    }, [fetchParents]);

    const handleDownloadTemplate = async () => {
        if (!schoolSlug) return;
        setIsDownloading(true);
        try {
            const response = await parentApi.downloadTemplate(schoolSlug, 'parents');
            if (response.success && response.data) {
                const contentDisposition = response.headers?.['content-disposition'];
                let fileName = 'parents_template.xlsx';
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename=(.+)/);
                    if (match && match[1]) {
                        fileName = match[1].replace(/['"]/g, '');
                    }
                }
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                showToast('Template downloaded successfully', 'success');
            } else {
                showToast(response.error || 'Failed to download template', 'error');
            }
        } catch (error) {
            showToast('Failed to download template', 'error');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !schoolSlug) return;

        // Validate file extension
        const allowedExtensions = ['xlsx', 'xls', 'csv'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        
        if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
            showToast('Please upload only Excel (.xlsx, .xls) or CSV (.csv) files.', 'error');
            if (importFileInputRef.current) {
                importFileInputRef.current.value = '';
            }
            return;
        }

        setImportFile(file);
        setIsImportPreviewOpen(true);
        if (importFileInputRef.current) {
            importFileInputRef.current.value = '';
        }
    };

    const handleOpenAddStudent = (parentId: string, parentName: string) => {
        setSelectedParentId(parentId);
        setSelectedParentName(parentName);
        
        // Reset search states
        setSearchAdmission('');
        setSearchedStudents([]);
        setSearchedStudent(null);
        setIsStudentSelected(false);
        setSearchPerformed(false);
        
        setIsAddStudentModalOpen(true);
    };

    const performSearch = useCallback(async (admissionNum: string) => {
        if (!admissionNum) return;

        setIsSearching(true);
        setSearchPerformed(true);
        setSearchedStudents([]);
        setSearchedStudent(null);
        setIsStudentSelected(false);

        try {
            const response = await parentApi.searchStudentByAdmission(schoolSlug, admissionNum);
            
            let studentsList: any[] = [];
            if (response.success && response.data) {
                if (Array.isArray(response.data)) {
                    studentsList = response.data;
                } else {
                    studentsList = [response.data];
                }
            } else if (response.data) {
                if (Array.isArray(response.data)) {
                    studentsList = response.data;
                } else {
                    studentsList = [response.data];
                }
            } else if (response && Array.isArray(response)) {
                studentsList = response;
            } else if (response && response.name && response.admission_number) {
                studentsList = [response];
            }

            setSearchedStudents(studentsList);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsSearching(false);
        }
    }, [schoolSlug]);


    // Debounced search logic
    useEffect(() => {
        if (!searchAdmission.trim()) {
            setSearchedStudents([]);
            setSearchedStudent(null);
            setIsStudentSelected(false);
            setSearchPerformed(false);
            return;
        }

        const delayDebounceFn = setTimeout(() => {
            performSearch(searchAdmission.trim());
        }, 500); // 500ms debounce

        return () => clearTimeout(delayDebounceFn);
    }, [searchAdmission, performSearch]);

    const handleLinkStudent = async () => {
        if (!schoolSlug || !selectedParentId || !searchedStudent) return;
        
        setIsAddingStudent(true);
        try {
            const response = await parentApi.assignStudentParent(schoolSlug, {
                student_id: searchedStudent.id,
                parent_user_id: selectedParentId,
                action: 'link'
            });
            if (response.success || response.status === 'success' || (response && !response.error)) {
                showToast(`Student ${searchedStudent.name} linked successfully!`, 'success');
                setIsAddStudentModalOpen(false);
                fetchParents();
            } else {
                showToast(response.error || 'Failed to link student', 'error');
            }
        } catch (error) {
            showToast('Failed to link student', 'error');
        } finally {
            setIsAddingStudent(false);
        }
    };

    const handleEditClick = (parent: Parent) => {
        setSelectedParent(parent);
        setEditName(parent.name || '');
        setEditEmail(parent.email || '');
        setEditPhone(parent.phone_number || '');
        setEditSecondaryPhone(parent.secondary_phone || '');
        setEditAddress(parent.address || '');
        setEditErrors({});
        setIsEditModalOpen(true);
    };

    const handleUpdateParent = async () => {
        if (!schoolSlug || !selectedParent) return;

        const errors: Record<string, string> = {};
        if (!editName.trim()) {
            errors.name = 'Parent name is required';
        }
        if (!editEmail.trim()) {
            errors.email = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editEmail.trim())) {
            errors.email = 'Invalid email address';
        }
        
        if (!editPhone.trim()) {
            errors.phone_number = 'Phone number is required';
        } else if (!/^[+]?[0-9]{10,15}$/.test(editPhone.trim())) {
            errors.phone_number = 'Phone number must be between 10 and 15 digits';
        }

        if (!editSecondaryPhone.trim()) {
            errors.secondary_phone = 'Secondary phone number is required';
        } else if (!/^[+]?[0-9]{10,15}$/.test(editSecondaryPhone.trim())) {
            errors.secondary_phone = 'Secondary phone number must be between 10 and 15 digits';
        }

        if (!editAddress.trim()) {
            errors.address = 'Address is required';
        }

        if (Object.keys(errors).length > 0) {
            setEditErrors(errors);
            return;
        }
        setEditErrors({});

        setIsUpdating(true);
        try {
            const response = await parentApi.updateParent(schoolSlug, selectedParent.user_id, {
                name: editName,
                email: editEmail,
                phone_number: editPhone,
                address: editAddress,
                secondary_phone: editSecondaryPhone
            });

            if (response.success) {
                showToast('Parent updated successfully', 'success');
                setIsEditModalOpen(false);
                fetchParents();
            } else {
                showToast(response.error || 'Failed to update parent', 'error');
            }
        } catch (error) {
            showToast('Failed to update parent', 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteClick = (parent: Parent) => {
        setParentToDelete(parent);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!schoolSlug || !parentToDelete) return;

        setIsDeleting(true);
        try {
            const response = await parentApi.deleteParent(schoolSlug, parentToDelete.user_id);
            if (response.success) {
                showToast('Parent deleted successfully', 'success');
                setIsDeleteModalOpen(false);
                fetchParents();
            } else {
                showToast(response.error || 'Failed to delete parent', 'error');
            }
        } catch (error) {
            showToast('Failed to delete parent', 'error');
        } finally {
            setIsDeleting(false);
        }
    };



    const handleDeleteStudentClick = (student: any) => {
        setStudentToDelete(student);
        setIsDeleteStudentModalOpen(true);
    };

    const handleConfirmDeleteStudent = async () => {
        if (!schoolSlug || !studentToDelete) return;

        setIsDeletingStudent(true);
        try {
            const response = await parentApi.assignStudentParent(schoolSlug, {
                student_id: studentToDelete.student_id || studentToDelete.id,
                parent_user_id: studentToDelete.parentId,
                action: 'delink'
            });
            if (response.success || response.status === 'success' || (response && !response.error)) {
                showToast('Student delinked successfully', 'success');
                setIsDeleteStudentModalOpen(false);
                fetchParents();
            } else {
                showToast(response.error || 'Failed to delink student', 'error');
            }
        } catch (error) {
            showToast('Failed to delink student', 'error');
        } finally {
            setIsDeletingStudent(false);
        }
    };

    const [collapsedParents, setCollapsedParents] = useState<Record<string, boolean>>({});

    const toggleParent = (parentId: string) => {
        setCollapsedParents(prev => ({
            ...prev,
            [parentId]: !prev[parentId]
        }));
    };

    // Flatten data for hierarchical view
    const flattenedData: any[] = [];
    parents.forEach(parent => {
        // Add parent as a node
        flattenedData.push({
            ...parent,
            isParent: true,
            id: parent.user_id, // Ensure unique ID for table
        });

        // Add children as sub-nodes if expanded
        if (!collapsedParents[parent.user_id]) {
            (parent.children || []).forEach(child => {
                flattenedData.push({
                    ...child,
                    isParent: false,
                    parentId: parent.user_id,
                    parentName: parent.name,
                    student_id: child.id, // Store database ID separately
                    id: `child-${parent.user_id}-${child.id}`, // Unique React key
                });
            });
        }
    });

    const columns: CustomTableColumn<any>[] = [
        {
            header: 'Name',
            key: 'name',
            sortable: true,
            width: '52%',
            minWidth: '280px',
            render: (item) => {
                const isParent = item.isParent;
                const isCollapsed = collapsedParents[item.user_id];
                const hasChildren = isParent && item.children && item.children.length > 0;

                if (isParent) {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); hasChildren && toggleParent(item.user_id); }}
                                disabled={!hasChildren}
                                style={{
                                    width: '26px', height: '26px', borderRadius: '6px',
                                    background: isCollapsed || !hasChildren ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: '1px solid var(--border-color)',
                                    color: isCollapsed || !hasChildren ? 'var(--text-muted)' : 'white',
                                    cursor: hasChildren ? 'pointer' : 'default', transition: 'all 0.2s', flexShrink: 0,
                                    opacity: hasChildren ? 1 : 0.5
                                }}
                                title={hasChildren ? (isCollapsed ? 'Show children' : 'Hide children') : 'No children'}
                            >
                                {isCollapsed || !hasChildren ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                            </button>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                                        {item.name || 'Unknown'}
                                    </span>
                                </div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'block', marginTop: '2px', wordBreak: 'break-word' }}>
                                    {item.email}
                                    {item.phone_number ? ` • ${item.phone_number}` : ''}
                                    {item.secondary_phone ? ` • Sec: ${item.secondary_phone}` : ''}
                                    {item.address ? ` • ${item.address}` : ''}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Student (child) row: Only show Name + Admission No + Gender (Academic details are in Column 2)
                const isMale = item.gender?.toUpperCase() === 'M' || item.gender?.toUpperCase() === 'MALE';
                const isFemale = item.gender?.toUpperCase() === 'F' || item.gender?.toUpperCase() === 'FEMALE';
                const genderLabel = isMale ? 'Male' : isFemale ? 'Female' : item.gender;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: 'clamp(0.75rem, 2vw, 1.75rem)', minWidth: 0 }}>
                        <CornerDownRight size={14} style={{ opacity: 0.5, flexShrink: 0, color: 'var(--accent-primary)' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', minWidth: 0, flex: 1 }}>
                            <div style={{
                                width: '32px', height: '32px', borderRadius: '50%',
                                background: 'var(--accent-primary-light, rgba(99, 102, 241, 0.1))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.8rem', fontWeight: 700,
                                color: 'var(--accent-primary, #4f46e5)',
                                flexShrink: 0
                            }}>
                                {initials(item.name || 'Student')}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary)' }}>
                                        {item.name}
                                    </span>
                                    {item.gender && (
                                        <span style={{
                                            fontSize: '0.65rem', padding: '1px 5px', borderRadius: '6px',
                                            background: isMale ? 'rgba(59, 130, 246, 0.1)' : isFemale ? 'rgba(236, 72, 153, 0.1)' : 'var(--bg-secondary)',
                                            border: `1px solid ${isMale ? 'rgba(59, 130, 246, 0.2)' : isFemale ? 'rgba(236, 72, 153, 0.2)' : 'var(--border-color)'}`,
                                            color: isMale ? '#2563eb' : isFemale ? '#db2777' : 'var(--text-secondary)',
                                            fontWeight: 600, whiteSpace: 'nowrap'
                                        }}>
                                            {genderLabel}
                                        </span>
                                    )}
                                </div>
                                {item.admission_number && (
                                    <span style={{ fontSize: '0.73rem', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                                        Adm No: {item.admission_number}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }
        },
        {
            header: 'Children / Academic Details',
            key: 'students' as any,
            width: '32%',
            minWidth: '160px',
            render: (item) => {
                if (!item.isParent) {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <span style={{
                                fontSize: '0.72rem',
                                padding: '2px 8px',
                                borderRadius: '8px',
                                background: 'var(--bg-secondary)',
                                border: '1px solid var(--border-color)',
                                color: 'var(--text-secondary)',
                                fontWeight: 600
                            }}>
                                {item.class || item.class_name || 'No Class'}
                                {item.section ? ` - Sec ${item.section}` : ''}
                            </span>
                            {item.roll_number && (
                                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                                    Roll: #{item.roll_number}
                                </span>
                            )}
                        </div>
                    );
                }
                
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {item.children && item.children.length > 0 ? (
                            item.children.map((child: any) => (
                                <span key={child.id} style={{
                                    fontSize: '0.65rem',
                                    background: 'rgba(99, 102, 241, 0.1)',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    color: 'rgb(79, 70, 229)',
                                    fontWeight: '600',
                                    border: '1px solid rgba(99, 102, 241, 0.15)'
                                }}>
                                    {child.name}
                                </span>
                            ))
                        ) : (
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No children</span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Actions',
            key: 'actions',
            align: 'right',
            width: '130px',
            minWidth: '130px',
            render: (item) => {
                const isParent = item.isParent;

                return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', flexShrink: 0, width: '100%', whiteSpace: 'nowrap' }}>
                        {isParent ? (
                            <>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleOpenAddStudent(item.user_id, item.name); }}
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        minWidth: '30px',
                                        minHeight: '30px',
                                        background: 'rgba(99, 102, 241, 0.1)',
                                        border: '1px solid rgba(99, 102, 241, 0.25)',
                                        cursor: 'pointer',
                                        color: 'var(--accent-primary)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '7px',
                                        transition: 'all 0.2s ease',
                                        flexShrink: 0,
                                        padding: 0
                                    }}
                                    title="Add Child"
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.background = 'var(--accent-primary)';
                                        e.currentTarget.style.color = '#ffffff';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
                                        e.currentTarget.style.color = 'var(--accent-primary)';
                                    }}
                                >
                                    <UserPlus size={15} strokeWidth={2.2} />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleEditClick(item); }}
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        minWidth: '30px',
                                        minHeight: '30px',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: 'var(--text-secondary)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '7px',
                                        transition: 'all 0.2s ease',
                                        flexShrink: 0,
                                        padding: 0
                                    }}
                                    title="Edit Parent"
                                    onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-tertiary, rgba(0,0,0,0.05))'}
                                    onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                                >
                                    <Pencil size={15} />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteClick(item); }}
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        minWidth: '30px',
                                        minHeight: '30px',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: 'var(--status-error, #ef4444)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '7px',
                                        transition: 'all 0.2s ease',
                                        flexShrink: 0,
                                        padding: 0
                                    }}
                                    title="Delete Parent"
                                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                                    onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                                >
                                    <Trash2 size={15} />
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteStudentClick(item); }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '3px 8px',
                                    color: 'var(--status-error, #ef4444)',
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}
                                title="Delink Student"
                                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                            >
                                Delink
                            </button>
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Parents', icon: <Users size={14} /> }
                ]}
            />
            <CustomCommonScreenHeader
                title="Parent Management"
                subtitle="View, manage and map parents to students."
                icon={<Users size={24} />}
                actionLabel="Add Parent"
                onActionClick={() => navigate(`/${schoolSlug}/school-admin/parents/invite`)}
            />

            <div className="table-filter-bar">
                {/* Search Box */}
                <div className="table-search-box">
                    <Search size={16} className="table-search-icon" />
                    <input
                        type="text"
                        placeholder="Search by parent name, email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            className="table-search-clear"
                            onClick={() => setSearch('')}
                            title="Clear search"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>

                {/* Import / Download Actions */}
                <div className="table-filter-actions">
                    <CustomButton
                        variant="outline"
                        size="small"
                        onClick={handleDownloadTemplate}
                        disabled={isDownloading}
                    >
                        <Download size={16} style={{ marginRight: '0.5rem' }} />
                        {isDownloading ? 'Downloading...' : 'Download Template'}
                    </CustomButton>
                    
                    <CustomButton
                        variant="outline"
                        size="small"
                        onClick={() => importFileInputRef.current?.click()}
                        disabled={isImporting}
                    >
                        <Upload size={16} style={{ marginRight: '0.5rem' }} />
                        {isImporting ? 'Importing...' : 'Import Parents'}
                    </CustomButton>
                    <input
                        type="file"
                        ref={importFileInputRef}
                        onChange={handleFileImport}
                        accept=".xlsx, .xls, .csv"
                        style={{ display: 'none' }}
                    />
                </div>
            </div>

            <CustomTable
                columns={columns}
                data={flattenedData}
                isLoading={isLoading}
                onRowClick={(item) => item.isParent && toggleParent(item.user_id)}
                isCompact={true}
                minWidth="580px"
                rowClassName={(item) => !item.isParent ? 'parent-child-subrow' : 'parent-header-row'}
                emptyMessage={debouncedSearch ? `No parents matching "${debouncedSearch}"` : 'No parents added yet'}
                emptyIcon={<UserPlus size={32} />}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={(colKey, order) => {
                    setSortBy(colKey);
                    setSortOrder(order);
                    setPage(1);
                }}
                pagination={{
                    currentPage: page,
                    totalPages: totalPages,
                    totalCount: totalCount,
                    pageSize: pageSize,
                    onPageChange: setPage,
                    hasPrevious: hasPrevious,
                    hasNext: hasNext,
                    isLoading: isLoading,
                    itemName: 'parents',
                }}
            />

            {/* Add Student Modal */}
            <CustomModal
                isOpen={isAddStudentModalOpen}
                onClose={() => !isAddingStudent && setIsAddStudentModalOpen(false)}
                title={`Link Student for ${selectedParentName}`}
                size="medium"
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', width: '100%' }}>
                        <CustomButton
                            variant="outline"
                            color="secondary"
                            onClick={() => setIsAddStudentModalOpen(false)}
                            disabled={isAddingStudent}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            variant="filled"
                            color="primary"
                            onClick={handleLinkStudent}
                            loading={isAddingStudent}
                            disabled={!isStudentSelected || !searchedStudent}
                        >
                            Link Student
                        </CustomButton>
                    </div>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', padding: '0.5rem 0' }}>
                    <CustomInput
                        name="searchAdmissionNum"
                        label="Search Student by Admission ID"
                        placeholder="Search student by admission ID"
                        value={searchAdmission}
                        onChange={(e) => setSearchAdmission(e.target.value)}
                        leftIcon={<Search size={18} style={{ color: 'var(--text-muted)' }} />}
                        rightIcon={isSearching ? <CustomLoading size="small" /> : undefined}
                    />

                    {!isSearching && searchedStudents.length > 0 && (
                        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                                Search Results ({searchedStudents.length} found)
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '300px', overflowY: 'auto', paddingRight: '4px' }}>
                                {searchedStudents.map((student) => {
                                    const isSelected = searchedStudent?.id === student.id;
                                    const isLinked = student.is_linked === true;
                                    const linkedParent = isLinked && student.parents && student.parents.length > 0 ? student.parents[0] : null;

                                    // Colors
                                    const borderColor = isLinked
                                        ? '2px solid #ef4444'
                                        : isSelected
                                            ? '2px solid #10b981'
                                            : '1px solid var(--border-color)';
                                    const bgColor = isLinked
                                        ? 'rgba(239, 68, 68, 0.04)'
                                        : isSelected
                                            ? 'rgba(16, 185, 129, 0.06)'
                                            : 'var(--bg-secondary)';
                                    const avatarBg = isLinked
                                        ? 'rgba(239, 68, 68, 0.12)'
                                        : 'rgba(16, 185, 129, 0.12)';
                                    const avatarColor = isLinked ? '#ef4444' : '#10b981';

                                    return (
                                        <div
                                            key={student.id}
                                            onClick={() => {
                                                if (isLinked) return; // block selecting already-linked students
                                                if (isSelected) {
                                                    setSearchedStudent(null);
                                                    setIsStudentSelected(false);
                                                } else {
                                                    setSearchedStudent(student);
                                                    setIsStudentSelected(true);
                                                }
                                            }}
                                            style={{
                                                border: borderColor,
                                                borderRadius: '10px',
                                                padding: isLinked ? '0.45rem 0.7rem' : '0.65rem 0.8rem',
                                                background: bgColor,
                                                cursor: isLinked ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '0.25rem',
                                                transition: 'all 0.2s ease',
                                                opacity: isLinked ? 0.85 : 1,
                                            }}
                                        >
                                            {/* Top row: avatar + info + badge/radio */}
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                                                    <div style={{
                                                        width: isLinked ? '28px' : '34px',
                                                        height: isLinked ? '28px' : '34px',
                                                        borderRadius: '50%',
                                                        background: avatarBg,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: avatarColor,
                                                        fontWeight: 700,
                                                        fontSize: isLinked ? '0.75rem' : '0.85rem',
                                                        flexShrink: 0
                                                    }}>
                                                        {student.name ? student.name.charAt(0).toUpperCase() : 'S'}
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                                        <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {student.name}
                                                        </span>
                                                        <span style={{ fontSize: '0.73rem', color: 'var(--text-secondary)' }}>
                                                            {student.class || 'N/A'} • Sec: {student.section || 'N/A'} • Adm: {student.admission_number || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Right side: badge or radio */}
                                                {isLinked ? (
                                                    <span style={{
                                                        fontSize: '0.68rem',
                                                        fontWeight: 700,
                                                        color: '#ef4444',
                                                        background: 'rgba(239,68,68,0.1)',
                                                        border: '1px solid rgba(239,68,68,0.25)',
                                                        borderRadius: '20px',
                                                        padding: '0.15rem 0.55rem',
                                                        whiteSpace: 'nowrap',
                                                        flexShrink: 0,
                                                        letterSpacing: '0.02em'
                                                    }}>
                                                        Already Linked
                                                    </span>
                                                ) : (
                                                    <div style={{
                                                        width: '18px',
                                                        height: '18px',
                                                        borderRadius: '50%',
                                                        border: `2px solid ${isSelected ? '#10b981' : 'var(--border-color)'}`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: isSelected ? '#10b981' : 'transparent',
                                                        color: 'white',
                                                        flexShrink: 0,
                                                        transition: 'all 0.2s'
                                                    }}>
                                                        {isSelected && <Check size={10} />}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Parent info row (only for linked students) */}
                                            {isLinked && linkedParent && (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.35rem',
                                                    background: 'rgba(239,68,68,0.06)',
                                                    borderRadius: '6px',
                                                    padding: '0.2rem 0.5rem',
                                                }}
                                                >
                                                    <GraduationCap size={11} color="#ef4444" style={{ flexShrink: 0 }} />
                                                    <span style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: 600 }}>
                                                        Linked to:
                                                    </span>
                                                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                                                        {linkedParent.name}{linkedParent.email ? ` · ${linkedParent.email}` : ''}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {!isSearching && searchPerformed && searchedStudents.length === 0 && (
                        <div style={{
                            textAlign: 'center',
                            padding: '2rem 1rem',
                            border: '1px dashed var(--border-color)',
                            borderRadius: '12px',
                            color: 'var(--text-muted)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem'
                        }}>
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>No student found</span>
                            <span>No student record matches the admission ID "{searchAdmission}"</span>
                        </div>
                    )}
                </div>
            </CustomModal>

            {/* Edit Parent Modal */}
            <CustomModal
                isOpen={isEditModalOpen}
                onClose={() => !isUpdating && setIsEditModalOpen(false)}
                title="Edit Parent Details"
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', width: '100%' }}>
                        <CustomButton
                            variant="outline"
                            color="secondary"
                            onClick={() => setIsEditModalOpen(false)}
                            disabled={isUpdating}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            variant="filled"
                            color="primary"
                            onClick={handleUpdateParent}
                            loading={isUpdating}
                        >
                            Save Changes
                        </CustomButton>
                    </div>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', padding: '1rem 0' }}>
                    <CustomInput
                        name="parentName"
                        label="Parent Name"
                        placeholder="Enter parent's full name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        required
                        error={editErrors.name}
                    />
                    <CustomInput
                        name="parentEmail"
                        label="Email Address"
                        placeholder="Enter parent's email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        required
                        error={editErrors.email}
                    />
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <CustomInput
                                name="parentPhone"
                                label="Phone Number"
                                placeholder="Enter phone number"
                                value={editPhone}
                                onChange={(e) => setEditPhone(e.target.value)}
                                required
                                error={editErrors.phone_number}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <CustomInput
                                name="parentSecondaryPhone"
                                label="Secondary Phone"
                                placeholder="Enter secondary phone number"
                                value={editSecondaryPhone}
                                onChange={(e) => setEditSecondaryPhone(e.target.value)}
                                required
                                error={editErrors.secondary_phone}
                            />
                        </div>
                    </div>
                    <CustomInput
                        name="parentAddress"
                        label="Address"
                        placeholder="Enter physical address"
                        value={editAddress}
                        onChange={(e) => setEditAddress(e.target.value)}
                        required
                        error={editErrors.address}
                    />
                </div>
            </CustomModal>



            {/* Delete Parent Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Parent Account"
                message={`Are you sure you want to delete the account for ${parentToDelete?.name}? This will revoke their access to the system and all associated children mappings.`}
                confirmLabel="Delete Account"
                variant="danger"
                isLoading={isDeleting}
            />

            {/* Delink Student Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteStudentModalOpen}
                onClose={() => setIsDeleteStudentModalOpen(false)}
                onConfirm={handleConfirmDeleteStudent}
                title="Delink Student from Parent"
                message={`Are you sure you want to delink ${studentToDelete?.name} from parent ${studentToDelete?.parentName || 'this parent'}?`}
                confirmLabel="Delink Student"
                variant="danger"
                isLoading={isDeletingStudent}
            />

            {importSummaryData && (
                <ImportSummaryModal
                    isOpen={isImportSummaryOpen}
                    onClose={() => {
                        setIsImportSummaryOpen(false);
                        setImportSummaryData(null);
                    }}
                    successCount={importSummaryData.success}
                    failedCount={importSummaryData.failed}
                    errors={importSummaryData.errors}
                    fileName={importSummaryData.fileName}
                    title="Parent Import Summary"
                />
            )}

            <ImportPreviewModal
                isOpen={isImportPreviewOpen}
                onClose={() => {
                    setIsImportPreviewOpen(false);
                    setImportFile(null);
                }}
                file={importFile}
                type="parents"
                onImport={(file) => parentApi.importFile(schoolSlug, 'parents', file)}
                onSuccess={() => {
                    fetchParents();
                }}
            />
        </div>
    );
};
